export function queryStringToObject(query: string): any {
  const pairs = query.slice(1).split("&");
  const result = {};
  pairs.forEach(pair => {
    const p = pair.split("=");
    if (p[0] !== "") {
      result[p[0]] = decodeURIComponent(p[1] || "");
    }
  });
  return JSON.parse(JSON.stringify(result));
}

let unqId = 0;
export function uniqId(prefix = "id") {
  // eslint-disable-next-line no-plusplus
  unqId++;
  return `${prefix}${unqId}`;
}

/**
 * Run a function if it is safe to do so when taking SSR into account.
 * @param func The function to run if it is safe to do so.
 * @param dependencies Any extra dependencies to check.
 */
export function ssrSafe<T>(
  func: () => T,
  dependencies: any[] = [
    (typeof process === "object" && process.browser) ||
      typeof window === "object"
  ]
) {
  // ensures browser specific functions are run only if browser is available whether in ssr or client
  if (dependencies.every(Boolean)) {
    return func();
  }

  return undefined;
}

export function makeSsrSafe<T extends CallableFunction>(
  func: T,
  dependencies?: any[]
): T {
  return function (this: ThisParameterType<T>, ...args: any[]) {
    return ssrSafe(() => (func as any).call(this, ...args), dependencies);
  } as any as T;
}

export const isIE = makeSsrSafe(() => Boolean(/MSIE|Trident/i.test(window.navigator.userAgent)));

/**
 * Debounce the passed function. This means that the function will only fire once while
 * it is being called within the threshold.
 * @param fn The function to be debounced
 * @param threshold How long to wait before allowing the function to be triggered again. Default is `100`.
 * @param immediate Whether to trigger the function at the start instead of after the threshold. Default is `false`.
 * @return A version of this function that will only trigger when it isn't called for the threshold amount of time.
 */
// tslint:disable-next-line: no-any
export const debounce = makeSsrSafe(
  <T extends (...args: any[]) => void>(
    fn: T,
    threshold = 100,
    immediate = false
  ) => {
    let timeout: number | undefined;

    return function (this: T, ...args: unknown[]) {
      const call = () => {
        timeout = undefined;

        if (!immediate) {
          return fn.apply(this, args);
        }
      };

      if (immediate && !timeout) {
        call();
      }

      clearTimeout(timeout!);
      timeout = window.setTimeout(call, threshold);
    } as T;
  }
);

/**
 * @param {string} parentTestId testId of parent component
 * @param {string} component Callee component's definition. Ex: Button
 * @param {string} testIdSuffix Describes callee component's feature/property. Ex: SelectPrice
 */
export function getTestId(
  parentTestId: string | undefined,
  component: string,
  testIdSuffix?: string
) {
  return parentTestId ?
    `${parentTestId}_${component}${testIdSuffix ? `_${testIdSuffix}` : ``}` :
    ``;
}
export function getNthNumber(num: number) {
  const lastDigit = num % 10,
    lastTwoDigit = num % 100;
  // eslint-disable-next-line eqeqeq
  if (lastDigit == 1 && lastTwoDigit != 11) {
    return `${num  }st`;
  }
  // eslint-disable-next-line eqeqeq
  if (lastDigit == 2 && lastTwoDigit != 12) {
    return `${num  }nd`;
  }
  // eslint-disable-next-line eqeqeq
  if (lastDigit == 3 && lastTwoDigit != 13) {
    return `${num  }rd`;
  }
  return `${num  }th`;
}

export function updateClassName(
  id,
  hideInDesktop,
  hideInTablet,
  hideInMobile,
  classes
) {
  const classElement = document.getElementById(id);
  if (classElement) {
    let classText = "";
    classText += hideInDesktop ? classes.hideInDesktop : "";
    classText += hideInTablet ? classes.hideInTablet : "";
    classText += hideInMobile ? classes.hideInMobile : "";
    if (classText !== "") {
      classElement.classList.add(classText);
    }
  }
}

export const suppressComponent = (component, options) => {
  if (
    // eslint-disable-next-line no-prototype-builtins
    !component.hasOwnProperty("isAuthenticatedUser") &&
    // eslint-disable-next-line no-prototype-builtins
    !component.hasOwnProperty("recognizedUserData")
  ) {
    return false;
  } else if (
    // eslint-disable-next-line eqeqeq
    component.isAuthenticatedUser == "visible" &&
    // eslint-disable-next-line eqeqeq
    component.recognizedUserData == "visible"
  ) {
    return false;
  } else if (
    // eslint-disable-next-line eqeqeq
    component.isAuthenticatedUser == "hidden" &&
    // eslint-disable-next-line eqeqeq
    component.recognizedUserData == "hidden"
  ) {
    if (options.isAuthenticatedUser || options.recognizedUserData) {
      return true;
    }
    return false;
  } else if (
    // eslint-disable-next-line eqeqeq
    component.isAuthenticatedUser == "visible" &&
    // eslint-disable-next-line eqeqeq
    component.recognizedUserData == "hidden"
  ) {
    if (typeof options.recognizedUserData === "undefined") {
      return false;
    }
    if (
      typeof options.recognizedUserData !== "undefined" &&
      options.recognizedUserData
    ) {
      return true;
    }
    return false;
  } else if (
    // eslint-disable-next-line eqeqeq
    component.isAuthenticatedUser == "hidden" &&
    // eslint-disable-next-line eqeqeq
    component.recognizedUserData == "visible"
  ) {
    if (
      typeof options.isAuthenticatedUser !== "undefined" &&
      options.isAuthenticatedUser
    ) {
      return true;
    }
    return false;
  }
  return false;
};

export const pluckArray = (arrList, key) => {
  // eslint-disable-next-line curly
  if(!arrList.length) return []
  return arrList.map(c => c[key])
}
export const chnlPkgReference = () => [
  {
    name: 'ATT_TV_ENTERTAINMENT',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_CHOICE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_ULTIMATE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_PREMIER',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_XTRA',
    categoryId: 'ENGLISH'
  },
  {
    name: 'ATT_TV_OPTIMO_MAS',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_ENTERTAINMENT',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_CHOICE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_ULTIMATE',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_PREMIER',
    categoryId: 'ENGLISH'
  },
  {
    name: 'DTV_OPTIMO_MAS',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_MAS_LATINO',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_MAS_ULTRA',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_LO_MAXIMO',
    categoryId: 'SPANISH'
  },
  {
    name: 'DTV_BASIC_CHOICE',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'DTV_PREFERRED_CHOICE_ALL_IN',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'DTV_PREFERRED_CHOICE',
    categoryId: 'WORLD_DIRECT'
  },
  {
    name: 'MYFREE_DTV',
    categoryId: 'ENGLISH'
  }
]

export interface ILatestCartOptions {
  flowTypeCookie?: string;
  enableConfigureCart?: boolean;
}

export function getLatestCartFromStorage(options: ILatestCartOptions = {}): any | null {
  const { flowTypeCookie, enableConfigureCart } = options;
  // eslint-disable-next-line curly
  if (typeof window !== 'object' || !window?.localStorage) return null;
  const newConfigureCartEnabled = enableConfigureCart && flowTypeCookie;
  const keys = newConfigureCartEnabled ?
    ['satellite', 'build'] :
    ['stream', 'satellite', 'genre'];

  const parsed = keys.map(k => {
    try {
      const raw = window.localStorage.getItem(k);
      // eslint-disable-next-line curly
      if (!raw) return null;
      const data = JSON.parse(raw);
      const lastUpdated = data?.packageDetail?.lastUpdated;
      const time = lastUpdated ? new Date(lastUpdated) : null;
      return {
        key: k,
        data,
        time
      };
    } catch {
      return null;
    }
  })?.filter(Boolean);

  // eslint-disable-next-line curly
  if (!parsed.length) return null;

  parsed.sort((a, b) => {
    // eslint-disable-next-line curly
    if (!a.time && !b.time) return 0;
    // eslint-disable-next-line curly
    if (!a.time) return 1;
    // eslint-disable-next-line curly
    if (!b.time) return -1;
    return b.time.getTime() - a.time.getTime();
  });

  let latest = parsed?.[0]?.data || null;
  const secondLatest = parsed?.[1]?.data || null;

  if (!newConfigureCartEnabled && latest?.cartType === 'genre' && latest?.genreBasePackages?.length === 0) {
    latest = secondLatest || null;
  }
  if (newConfigureCartEnabled) {
    const emptySignatureCart = ['signature', 'streaming'].includes(latest?.cartType) && isEmptyObj(latest?.package);
    const emptyGenreCart = latest?.cartType === 'genre' && (latest?.genreBasePackages?.length ?? 0) === 0;
    if (emptySignatureCart || emptyGenreCart) {
      latest = null;
    }
  }
  return latest;
}

export const isEmptyObj = (obj: any): boolean => !obj || typeof obj !== 'object' || Object.keys(obj).length === 0;

export const findDuplicateIds = () => {
  const allElementIds = document.querySelectorAll<HTMLElement>('[id]');

  const idSet = new Set<string>();
  const duplicateIds: Record<string, number> = {};
  const malformedIds: string[] = [];

  allElementIds.forEach(element => {
    // eslint-disable-next-line prefer-destructuring
    const id = element.id;
    // eslint-disable-next-line curly
    if(id === '') return; // Skip empty IDs
    if(idSet.has(id)) {
      if(id in duplicateIds) {
        duplicateIds[id] = duplicateIds[id] + 1;
      } else {
        duplicateIds[id] = 2;
      }
    } else {
      idSet.add(id);
    }
    if(id.includes(' ') || id.includes('undefined')) {
      malformedIds.push(id);
    }
  });

  if(Object.keys(duplicateIds).length > 0 || malformedIds.length > 0) {
    console.warn('Duplicate/ incorrectly formatted IDs found', "\nDuplicates (id, occurrences): ", duplicateIds, "\nIncorrectly Formatted (id):", malformedIds);
  } else {
    console.log('No duplicate/ incorrectly formatted IDs found');
  }

  console.warn("\nAll Existing Unique IDs (Please do not use in new components): ", [...idSet]);
}
